//stage52Gallery.js

import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import {
  STAGE52_GALLERY_BASE,
  STAGE52_GALLERY_CAPTIONS,
  STAGE52_GALLERY_ROW_COUNTS,
} from '../constants';

function buildStage52Rows(counts, base) {
  return counts.map((count, rowIdx) => {
    const rowNumber = rowIdx + 1;
    return Array.from({ length: count }, (_, i) => {
      return `${base}/row${rowNumber}-${i + 1}.webp`;
    });
  });
}

function renderGrid(grid, rows) {
  rows.forEach((srcList, rowIdx) => {
    const rowNumber = rowIdx + 1;

    const rowEl = document.createElement('div');
    rowEl.className = 'gallery-row';
    rowEl.dataset.row = String(rowNumber);

    const viewport = document.createElement('div');
    viewport.className = 'gallery-viewport swiper';

    const track = document.createElement('div');
    track.className = 'gallery-track swiper-wrapper';

    srcList.forEach((src, i) => {
      const index = i + 1;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gallery-thumb swiper-slide';
      btn.dataset.action = 'open-viewer';
      btn.dataset.row = String(rowNumber);
      btn.dataset.index = String(index);

      const img = document.createElement('img');
      img.src = src;
      img.alt = '';
      img.loading = 'lazy';

      btn.appendChild(img);
      track.appendChild(btn);
    });

    viewport.appendChild(track);

    const nextBtn = document.createElement('button');
    nextBtn.className = 'row-next';

    const nextImg = document.createElement('img');
    nextImg.src = '/images/arrow-right.png';
    nextImg.alt = '';
    nextBtn.appendChild(nextImg);

    rowEl.appendChild(viewport);
    rowEl.appendChild(nextBtn);

    grid.appendChild(rowEl);
  });
}

function toFlatIndex(rows, row1Based, index1Based) {
  let offset = 0;
  for (let r = 0; r < row1Based - 1; r++) offset += rows[r]?.length || 0;
  return offset + (index1Based - 1);
}

function getCaption(row1Based, index1Based) {
  const rules = STAGE52_GALLERY_CAPTIONS[row1Based];
  if (!rules) return '';

  for (const r of rules) {
    const [a, b] = r.range;
    if (index1Based >= a && index1Based <= b) return r.text || '';
  }
  return '';
}

export function mountStage52Gallery({ root, onOpenViewer } = {}) {
  const host = root?.querySelector?.('[data-gallery]');
  const grid = host?.querySelector?.('[data-gallery-grid]');
  const viewer = host?.querySelector?.('[data-gallery-viewer]');
  const toggleBtn = host?.querySelector?.('[data-action="toggle-gallery"]');

  const viewerSwiperEl = host?.querySelector?.('[data-viewer-swiper]');
  const viewerWrapper = host?.querySelector?.('[data-viewer-wrapper]');
  const captionEl = host?.querySelector?.('[data-viewer-caption]');

  const prevBtn = viewer?.querySelector?.('[data-action="viewer-prev"]');
  const nextBtn = viewer?.querySelector?.('[data-action="viewer-next"]');
  if (!host || !grid || !viewer || !viewerSwiperEl || !viewerWrapper)
    return null;

  // 데이터
  const rows = buildStage52Rows(
    STAGE52_GALLERY_ROW_COUNTS,
    STAGE52_GALLERY_BASE,
  );
  const flat = rows.flat();

  // 초기 mode
  host.dataset.mode = host.dataset.mode || 'grid';

  // grid 렌더
  grid.replaceChildren();
  renderGrid(grid, rows);

  // grid 스와이퍼
  const rowSwipers = [];
  grid.querySelectorAll('.gallery-row').forEach((rowEl) => {
    const viewport = rowEl.querySelector('.gallery-viewport.swiper');
    const navNextBtn = rowEl.querySelector('.row-next');
    if (!viewport) return;

    const s = new Swiper(viewport, {
      modules: [Navigation],
      slidesPerView: 4,
      spaceBetween: 15,
      speed: 220,
      loop: true,
      watchOverflow: true,
      preventClicks: true,
      preventClicksPropagation: true,
      navigation: navNextBtn
        ? {
            nextEl: navNextBtn,
          }
        : undefined,
      breakpoints: {
        768: {
          spaceBetween: 15,
        },
        0: {
          spaceBetween: 6,
        },
      },
    });

    rowEl.__swiper = s;
    rowSwipers.push(s);
  });

  // viewer 슬라이드
  viewerWrapper.replaceChildren();
  flat.forEach((src) => {
    const slide = document.createElement('div');
    slide.className = 'swiper-slide';

    const img = document.createElement('img');
    img.className = 'viewer-img';
    img.src = src;
    img.alt = '';
    img.loading = 'lazy';

    slide.appendChild(img);
    viewerWrapper.appendChild(slide);
  });

  // viewer 스와이퍼
  const viewerSwiper = new Swiper(viewerSwiperEl, {
    modules: [Navigation],
    slidesPerView: 1,
    speed: 220,
    loop: true,
    watchOverflow: true,
    navigation:
      prevBtn && nextBtn ? { prevEl: prevBtn, nextEl: nextBtn } : undefined,
    preventClicks: true,
    preventClicksPropagation: true,
  });

  viewerSwiper.on('slideChange', () => {
    const realIndex = viewerSwiper.realIndex;

    let acc = 0;
    let foundRow = 1;
    let foundIndex = 1;

    for (let r = 0; r < rows.length; r++) {
      const len = rows[r].length;
      if (realIndex < acc + len) {
        foundRow = r + 1;
        foundIndex = realIndex - acc + 1;
        break;
      }
      acc += len;
    }
    captionEl.textContent = getCaption(foundRow, foundIndex);
  });

  function setMode(mode) {
    host.dataset.mode = mode;
    if (mode === 'grid') {
      rowSwipers.forEach((s) => s.update());
    } else {
      viewerSwiper.update();
    }
  }

  function openViewerAt(flatIndex0, row, index) {
    setMode('viewer');
    viewerSwiper.slideToLoop(flatIndex0, 0);

    const caption = getCaption(row, index);
    captionEl.textContent = caption;
  }

  function closeViewer() {
    setMode('grid');
  }

  function onGridClick(e) {
    const btn = e.target.closest('button[data-action="open-viewer"]');
    if (!btn || !grid.contains(btn)) return;

    const rowEl = btn.closest('.gallery-row');
    const s = rowEl?.__swiper;
    if (s && s.allowClick === false) return;

    const row = Number(btn.dataset.row);
    const index = Number(btn.dataset.index);
    if (!Number.isFinite(row) || !Number.isFinite(index)) return;

    const flatIndex = toFlatIndex(rows, row, index);
    openViewerAt(flatIndex, row, index);
  }

  function onToggle(e) {
    e.preventDefault();
    if (host.dataset.mode === 'viewer') closeViewer();
  }
  grid.addEventListener('click', onGridClick, true);
  toggleBtn?.addEventListener('click', onToggle);

  function refreshAll() {
    rowSwipers.forEach((s) => s.update());
    viewerSwiper.update();
  }

  function destroy() {
    grid.removeEventListener('click', onGridClick, true);
    toggleBtn?.removeEventListener('click', onToggle);

    rowSwipers.forEach((s) => s.destroy(true, true));
    viewerSwiper.destroy(true, true);
  }

  return { destroy, rows, refreshAll, openViewerAt, closeViewer, viewerSwiper };
}
